import React, { useEffect, useState } from "react";
import Bugsnag from "@bugsnag/js";
import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import "firebase/compat/storage";
import { consola } from "consola";

import { nanoid } from "nanoid";

import {
  getFirebaseRecordFromRecipe,
  getIngredientsFromRecipe,
} from "../data/RecipeData";

const useRecipes = ({
  organizationId,
  publicOnly = false,
  skipIngredientsLoad = false,
}) => {
  const subscription = React.useRef();
  const subscriptionMealPlans = React.useRef();
  const subscriptionIngredients = React.useRef();

  const [recipes, setRecipes] = useState([]);
  const [recipesLoading, setRecipesLoading] = useState(true);
  const [mealPlans, setMealPlans] = useState([]);
  const [mealPlansLoading, setMealPlansLoading] = useState(true);
  const [ingredients, setIngredients] = useState([]);
  const [ingredientsLoading, setIngredientsLoading] = useState(true);

  /**
   * Fetches the recipes from the database
   */
  useEffect(() => {
    if (!organizationId) {
      return;
    }

    if (subscription.current) {
      subscription.current();
    }

    let subscriptionRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("recipes")
      .where("isArchived", "==", false);

    // If publicOnly is true, only fetch public recipes
    if (publicOnly) {
      subscriptionRef = subscriptionRef.where("isPublic", "==", true);
    }

    subscriptionRef = subscriptionRef.orderBy("createdOn", "desc");

    subscription.current = subscriptionRef.onSnapshot((snapshot) => {
      consola.debug("++++++ SNAPSHOT -> useRecipes: recipesSubscription");
      const data = snapshot.docs;

      const finalData = data.map((d) => ({
        ...d.data(),
        id: d.id,
      }));

      setRecipes(finalData);
      setRecipesLoading(false);
    }, (error) => {
      consola.error("useRecipes recipes snapshot error:", error);
      setRecipesLoading(false);
      setRecipes([]);
    });
  }, [organizationId, publicOnly]);

  /**
   * Fetches the meal Plans from the database
   */
  useEffect(() => {
    if (!organizationId) {
      return;
    }

    if (subscriptionMealPlans.current) {
      subscriptionMealPlans.current();
    }

    let subscriptionRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("mealPlans")
      .where("isArchived", "==", false);

    // If publicOnly is true, only fetch public meal plans
    if (publicOnly) {
      subscriptionRef = subscriptionRef.where("isPublic", "==", true);
    }

    subscriptionRef = subscriptionRef.orderBy("createdOn", "desc");

    subscriptionMealPlans.current = subscriptionRef.onSnapshot((snapshot) => {
      consola.debug("++++++ SNAPSHOT -> useRecipes: subscriptionMealPlans");
      const data = snapshot.docs;

      const finalData = data.map((d) => ({
        ...d.data(),
        id: d.id,
      }));
      setMealPlans(finalData);
      setMealPlansLoading(false);
    }, (error) => {
      consola.error("useRecipes mealPlans snapshot error:", error);
      setMealPlansLoading(false);
      setMealPlans([]);
    });
  }, [organizationId, publicOnly]);

  useEffect(() => {
    // Skip ingredients loading if flag is set
    if (skipIngredientsLoad) {
      return;
    }

    if (!organizationId) {
      return;
    }

    if (subscriptionIngredients.current) {
      subscriptionIngredients.current();
    }

    let subscriptionIngredientsRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("ingredients");

    // If publicOnly is true, only fetch public ingredients
    if (publicOnly) {
      subscriptionIngredientsRef = subscriptionIngredientsRef.where(
        "isPublic",
        "==",
        true,
      );
    }

    subscriptionIngredientsRef = subscriptionIngredientsRef.orderBy(
      "name",
      "asc",
    );

    subscriptionIngredients.current = subscriptionIngredientsRef.onSnapshot(
      (snapshot) => {
        consola.debug("++++++ SNAPSHOT -> useRecipes: subscriptionIngredients");
        const data = snapshot.docs;

        const finalData = data.map((d) => {
          return d.data();
        });
        setIngredients(finalData);
        setIngredientsLoading(false);
      },
      (error) => {
        consola.error("useRecipes ingredients snapshot error:", error);
        setIngredientsLoading(false);
        setIngredients([]);
      }
    );
  }, [organizationId, publicOnly, skipIngredientsLoad]);

  const ingredientDictionary = React.useMemo(() => {
    const dictionary = {};
    ingredients.forEach((ingredient) => {
      dictionary[ingredient.id] = ingredient;
    });
    return dictionary;
  }, [ingredients]);

  const recipeDictionary = React.useMemo(() => {
    const dictionary = {};
    recipes.forEach((recipe) => {
      dictionary[recipe.id] = recipe;
    });
    return dictionary;
  }, [recipes]);

  const mealPlanDictionary = React.useMemo(() => {
    const dictionary = {};
    mealPlans.forEach((mealPlan) => {
      dictionary[mealPlan.id] = mealPlan;
    });
    return dictionary;
  }, [mealPlans]);

  const editRecipe = React.useCallback(
    async function (recipeData) {
      // Add the log data here.
      console.log("useRecipes.editRecipe: Called with recipe data", {
        id: recipeData.id,
        name: recipeData.name,
        mealType: recipeData.mealType,
        isArchived: recipeData.isArchived,
        hasId: !!recipeData.id,
        recipeData: recipeData
      });
      
      // CRITICAL VALIDATION: Recipe must have an ID to edit
      if (!recipeData.id) {
        console.error("useRecipes.editRecipe: CRITICAL - Recipe data missing ID!", recipeData);
        Bugsnag.notify(new Error("Recipe data missing ID when trying to edit"));
        throw new Error("Cannot edit recipe without an ID. This should be a new recipe.");
      }
      
      // CRITICAL VALIDATION: Ensure ID is a valid string
      if (typeof recipeData.id !== 'string' || recipeData.id.trim() === '') {
        console.error("useRecipes.editRecipe: CRITICAL - Invalid recipe ID!", {
          id: recipeData.id,
          type: typeof recipeData.id
        });
        Bugsnag.notify(new Error("Invalid recipe ID when trying to edit"));
        throw new Error("Invalid recipe ID");
      }
      
      // Safety check: prevent editing archived recipes
      if (recipeData.isArchived) {
        console.error("useRecipes.editRecipe: CRITICAL - Attempted to edit archived recipe!", {
          id: recipeData.id,
          name: recipeData.name
        });
        Bugsnag.notify(new Error("Attempted to edit archived recipe"));
        throw new Error("Cannot edit archived recipes");
      }
      
      // MUTATION LOCATION: This is where the actual Firestore write happens
      // CRITICAL: The document ID MUST match the recipe's id property
      const ref = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("recipes")
        .doc(recipeData.id);

      // CRITICAL: Verify the document exists before updating to prevent creating duplicates
      try {
        const docSnapshot = await ref.get();
        const documentExists = docSnapshot.exists;
        
        console.log("useRecipes.editRecipe: Checking if document exists", {
          id: recipeData.id,
          path: ref.path,
          exists: documentExists,
          existingData: documentExists ? docSnapshot.data() : null
        });
        
        if (!documentExists) {
          console.error("useRecipes.editRecipe: CRITICAL - Document does not exist! This would create a duplicate!", {
            id: recipeData.id,
            name: recipeData.name,
            path: ref.path
          });
          Bugsnag.notify(new Error(`Attempted to edit non-existent recipe: ${recipeData.id}`));
          throw new Error(`Recipe with ID ${recipeData.id} does not exist. Cannot edit non-existent recipe.`);
        }
        
        // Verify the existing document's ID matches
        const existingData = docSnapshot.data();
        if (existingData.id && existingData.id !== recipeData.id) {
          console.error("useRecipes.editRecipe: CRITICAL - Document ID mismatch!", {
            documentId: recipeData.id,
            existingRecipeId: existingData.id,
            path: ref.path
          });
          Bugsnag.notify(new Error(`Recipe ID mismatch: document ID ${recipeData.id} doesn't match recipe.id ${existingData.id}`));
          throw new Error("Recipe ID mismatch detected");
        }
        
        consola.debug("+++++ WRITE => useRecipes: editRecipe", {
          id: recipeData.id,
          path: ref.path,
          exists: true
        });
        console.log("useRecipes.editRecipe: MUTATION - Updating existing Firestore document", {
          id: recipeData.id,
          path: ref.path,
          mealType: recipeData.mealType,
          isArchived: recipeData.isArchived,
          documentId: ref.id,
          recipeIdProperty: recipeData.id,
          idsMatch: ref.id === recipeData.id
        });
        
        // CRITICAL: Ensure the recipe's id property matches the document ID
        // This prevents duplicates where the id property doesn't match the doc ID
        const recipeToSave = {
          ...recipeData,
          id: ref.id, // Force the id property to match the document ID
        };
        
        // ACTUAL MUTATION: Firestore set with merge - updates existing document
        await ref.set(recipeToSave, { merge: true });
        console.log("useRecipes.editRecipe: Successfully updated recipe", {
          id: recipeData.id,
          documentId: ref.id,
          savedId: recipeToSave.id
        });
      } catch (e) {
        // If it's our validation error, re-throw it
        if (e.message && (e.message.includes("does not exist") || e.message.includes("ID mismatch"))) {
          throw e;
        }
        console.error("useRecipes.editRecipe: Error saving recipe", e, recipeData);
        Bugsnag.notify(new Error("Error saving recipe data"));
        throw e; // Re-throw to allow caller to handle
      }
    },
    [organizationId],
  );

  const editMealPlan = React.useCallback(
    async function (mealPlanData) {
      // Add the log data here.
      const ref = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("mealPlans")
        .doc(mealPlanData.id);

      try {
        consola.debug("+++++ WRITE => useRecipes: editMealPlan");
        await ref.set(mealPlanData, { merge: true });
      } catch (e) {
        Bugsnag.notify(new Error("Error saving meal plan data"));
      }
    },
    [organizationId],
  );

  const archiveRecipe = React.useCallback(
    async function (recipeId) {
      // Add the log data here.
      const ref = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("recipes")
        .doc(recipeId);

      try {
        consola.debug("+++++ WRITE => useRecipes: archiveRecipe");
        await ref.set({ isArchived: true }, { merge: true });
      } catch (e) {
        Bugsnag.notify(new Error("Error saving recipe data"));
      }
    },
    [organizationId],
  );

  const archiveMealPlan = React.useCallback(
    async function (mealPlanId) {
      // Add the log data here.
      const ref = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("mealPlans")
        .doc(mealPlanId);

      try {
        consola.debug("+++++ WRITE => useRecipes: archiveMealPlan");
        await ref.set({ isArchived: true }, { merge: true });
      } catch (e) {
        Bugsnag.notify(new Error("Error saving meal plan data"));
      }
    },
    [organizationId],
  );

  const editIngredient = React.useCallback(
    async function (ingredientData) {
      // Add the log data here.
      const ref = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("ingredients")
        .doc(ingredientData.id);

      try {
        consola.debug("+++++ WRITE => useRecipes: editIngredient");
        await ref.set(ingredientData, { merge: true });
      } catch (e) {
        Bugsnag.notify(new Error("Error saving ingredient data"));
      }
    },
    [organizationId],
  );

  const getRecipesFromList = React.useCallback(
    async function (recipes) {
      // Add the log data here.
      consola.debug("++++++ READ -> useRecipes: getRecipesFromList");
      const result = await Promise.all(
        recipes.map(async (recipe) => {
          // Query by the recipe's id field, not document ID
          const querySnapshot = await firebase
            .firestore()
            .collection("organizations")
            .doc(organizationId)
            .collection("recipes")
            .where("id", "==", recipe.recipeId)
            .get();

          if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            return {
              ...doc.data(),
              recipeId: recipe.id,
              days: recipe.days,
            };
          }

          return null;
        }),
      );

      return result;
    },
    [organizationId],
  );

  function getFileExtension(file) {
    if (file.type === "image/png") {
      return "png";
    }
    if (file.type === "image/jpeg") {
      return "jpg";
    }
    return "jpg";
  }

  const uploadImage = React.useCallback(
    async function ({ image, recipe, onStart, onProgress, onComplete }) {
      // Add the log data here.
      if (!image) {
        return;
      }
      if (!recipe) {
        return;
      }
      if (!recipe.id) {
        return;
      }
      if (!organizationId) {
        return;
      }
      if (onStart) {
        onStart();
      }
      const path = `/${organizationId}/recipe_images/${
        recipe.id
      }.${getFileExtension(image)}`;
      const uploadTask = firebase.storage().ref(path).put(image, {
        cacheControl: "private, max-age=3600",
      });

      await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            // progress function ...
            if (onProgress) {
              const progress = Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
              );
              onProgress(progress);
            }
          },
          (error) => {
            reject(error);
            if (onComplete) {
              onComplete();
            }
          },
          () => {
            firebase
              .storage()
              .ref(path)
              .getDownloadURL()
              .then(async (url) => {
                const ref = firebase
                  .firestore()
                  .collection("organizations")
                  .doc(organizationId)
                  .collection("recipes")
                  .doc(recipe.id);

                // Update the user to include the image.
                consola.debug("+++++ WRITE => useRecipes: uploadImage");
                await ref.set(
                  {
                    imageUrl: url,
                    updatedOn: new Date(),
                  },
                  { merge: true },
                );

                if (onComplete) {
                  onComplete();
                }
                resolve(true);
              })
              .catch((e) => {
                reject(e);
              });
          },
        );
      });
    },
    [organizationId],
  );

  const getIngredientById = React.useCallback(
    (id) => {
      if (!id) {
        return null;
      }
      return ingredientDictionary[id];
    },
    [ingredientDictionary],
  );

  const getRecipeById = React.useCallback(
    (id) => {
      if (!id) {
        return null;
      }
      return recipeDictionary[id];
    },
    [recipeDictionary],
  );

  const getMealPlanById = React.useCallback(
    (id) => {
      if (!id) {
        return null;
      }
      return mealPlanDictionary[id];
    },
    [mealPlanDictionary],
  );

  const findIngredientByName = async (organizationId, name) => {
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("ingredients")
      .where("name", "==", name);
    consola.debug("++++++ READ -> useRecipes: findIngredientByName");
    const snapshot = await ref.get();
    if (snapshot.empty) {
      return null;
    }

    const data = snapshot.docs[0].data();
    return data;
  };

  const addIngredientByObject = async (organizationId, ingredient) => {
    // check to see if ingredient exists
    const existingIngredient = await findIngredientByName(
      organizationId,
      ingredient.name,
    );
    if (existingIngredient) {
      return existingIngredient;
    }

    // Add the log data here.
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("ingredients")
      .doc(ingredient.id);

    const baseIngredient = {
      id: ingredient.id,
      name: ingredient.name,
      unit: ingredient.unit,
      proteins: ingredient.proteins,
      carbs: ingredient.carbs,
      fats: ingredient.fats,
      createdOn: new Date(),
      updatedOn: new Date(),
    };

    try {
      consola.debug("+++++ WRITE => useRecipes: addingIngredientByObject");
      await ref.set(baseIngredient, { merge: true });
    } catch (e) {
      Bugsnag.notify(new Error("Error saving ingredient data"));
    }

    return baseIngredient;
  };

  const addRecipeFromObject = async (organizationId, recipeData) => {
    console.log(
      "+++++ WRITE => useRecipes: addRecipeFromObject",
      JSON.stringify(recipeData, null, 2),
    );
    
    // CRITICAL: Use the ID from recipeData if provided (from dialog), otherwise generate new one
    // This ensures the document ID matches the recipe's id property
    const recipeId = recipeData.id || nanoid();
    
    console.log("useRecipes.addRecipeFromObject: Creating recipe", {
      providedId: recipeData.id,
      usingId: recipeId,
      willGenerateNew: !recipeData.id
    });
    
    // CRITICAL: Check if document already exists to prevent duplicates
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("recipes")
      .doc(recipeId);
    
    const docSnapshot = await ref.get();
    if (docSnapshot.exists) {
      console.error("useRecipes.addRecipeFromObject: CRITICAL - Document already exists! This would create a duplicate!", {
        id: recipeId,
        name: recipeData.name,
        existingData: docSnapshot.data()
      });
      Bugsnag.notify(new Error(`Attempted to create recipe with existing ID: ${recipeId}`));
      throw new Error(`Recipe with ID ${recipeId} already exists. Cannot create duplicate.`);
    }
    
    // Add ingredients to the database.
    const ingredients = getIngredientsFromRecipe(recipeData);

    // Add them to the ingredient collection
    const promises = ingredients.map((ingredient) => {
      return addIngredientByObject(organizationId, ingredient);
    });
    await Promise.all(promises);

    // Use the recipe data directly if it's already in the correct format (from dialog)
    // Otherwise convert from AI format using getFirebaseRecordFromRecipe
    let recipe;
    if (recipeData.name && recipeData.mealType !== undefined) {
      // Already in Firebase format (from dialog)
      recipe = {
        ...recipeData,
        id: recipeId, // Ensure ID matches document ID
        ingredients: ingredients.map((ingredient) => {
          const id = nanoid();
          return {
            ...ingredient,
            id,
            ingredientId: ingredient.id,
          };
        }),
      };
    } else {
      // AI format - convert it
      recipe = getFirebaseRecordFromRecipe(recipeData);
      recipe.id = recipeId; // Use the provided/generated ID
      recipe.ingredients = ingredients.map((ingredient) => {
        const id = nanoid();
        return {
          ...ingredient,
          id,
          ingredientId: ingredient.id,
        };
      });
    }
    
    console.log("useRecipes.addRecipeFromObject: Creating new recipe document", {
      id: recipeId,
      documentId: ref.id,
      idsMatch: ref.id === recipeId,
      recipeIdProperty: recipe.id
    });
    
    consola.debug("+++++ WRITE => useRecipes: addRecipeFromObject");
    // CRITICAL: Ensure the recipe's id property matches the document ID
    recipe.id = ref.id;
    await ref.set(recipe, { merge: true });
    
    console.log("useRecipes.addRecipeFromObject: Successfully created recipe", {
      id: recipeId,
      documentId: ref.id
    });
  };

  return {
    recipes,
    recipesLoading,
    mealPlans,
    mealPlansLoading,
    ingredients,
    ingredientsLoading,
    editRecipe,
    editMealPlan,
    archiveRecipe,
    archiveMealPlan,
    editIngredient,
    uploadImage,
    getRecipesFromList,
    getIngredientById,
    getRecipeById,
    getMealPlanById,
    addRecipeFromObject,
  };
};

export default useRecipes;
